import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface ArenaMessage {
  id: string;
  arena_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  sender?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export const useArenaChat = (arenaId: string | null) => {
  const { user } = useAuthContext();
  const [messages, setMessages] = useState<ArenaMessage[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!arenaId) return;

    setLoading(true);
    const { data, error } = await supabase
      .from("arena_messages")
      .select(`
        *,
        profiles:sender_id (
          id,
          name,
          photo_url
        )
      `)
      .eq("arena_id", arenaId)
      .order("created_at", { ascending: true })
      .limit(100);

    if (!error && data) {
      setMessages(data.map(m => ({
        ...m,
        sender: m.profiles as ArenaMessage["sender"],
      })));
    }
    setLoading(false);
  }, [arenaId]);

  // Subscribe to new messages
  useEffect(() => {
    if (!arenaId) return;

    fetchMessages();

    const channel = supabase
      .channel(`arena_chat_${arenaId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "arena_messages",
          filter: `arena_id=eq.${arenaId}`,
        },
        async (payload) => {
          // Fetch sender info
          const { data: profile } = await supabase
            .from("profiles")
            .select("id, name, photo_url")
            .eq("id", (payload.new as ArenaMessage).sender_id)
            .single();

          const newMessage: ArenaMessage = {
            ...(payload.new as ArenaMessage),
            sender: profile || undefined,
          };

          setMessages(prev => [...prev, newMessage]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId, fetchMessages]);

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    if (!user || !arenaId || !content.trim()) return { error: new Error("Invalid message") };

    const { error } = await supabase
      .from("arena_messages")
      .insert({
        arena_id: arenaId,
        sender_id: user.id,
        content: content.trim(),
      });

    return { error };
  }, [user, arenaId]);

  return {
    messages,
    loading,
    sendMessage,
  };
};
